import { useState } from "react";
import { BETRIEBSFRISTEN, istEntwicklung } from "./konsole-nav";
import { useKonsolenRolle } from "./rolle";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { Dialog, useAction } from "../../components/ui";
import { DEFAULT_RANGES, PURGE_KEYS, purgeImpact } from "./model";
import { Basiszins } from "./Basiszins";
import { ZweiterFaktorBetrieb } from "./ZweiterFaktorBetrieb";
import "./betrieb.css";

/**
 * Ergänzungen der Betreiber-Konsole (0050): Zustellung je Agentur, Lebenszyklus (Kündigung, Nur-Lesen-Phase, Löschplan)
 * und Produkt-Standards. Der Betrieb sieht Zählwerte und Zustände, nie Inhalte der Agenturen.
 */

export interface MailStat { org_id: string; sent_30d: number; failed_30d: number; pending: number; last_failed_at: string | null; last_error: string | null }

export function useMailStats(orgId: string | null = null, enabled = true) {
  return useQuery({
    queryKey: ["platform_mail_stats", orgId], enabled,
    queryFn: () => api.read<MailStat[]>("platform_mail_stats", orgId ? { p_org: orgId } : {}).then((r) => r ?? []),
  });
}

const dt = (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeZone: "UTC" }).format(new Date(iso)) : "–");

/** Letzter Fehler mit Datum; ohne Zeitpunkt ohne „am –“ (SICHT-OEFFENTLICH-BETRIEB-09). */
export function lastFailureText(s: Pick<MailStat, "last_failed_at" | "last_error">): string {
  const msg = s.last_error ?? "ohne Meldung";
  return s.last_failed_at ? `Letzter Fehler am ${dt(s.last_failed_at)}: ${msg}` : `Letzter Fehler: ${msg}`;
}

export function MailPill({ s }: { s: MailStat | undefined }) {
  if (!s) return <>–</>;
  if (s.failed_30d > 0) return <span className="pill error" title={s.last_error ?? undefined}><span className="pill-zeichen">!</span> {s.failed_30d} gescheitert</span>;
  return <span className="pill success"><span className="pill-zeichen">✓</span> {s.sent_30d} zugestellt</span>;
}

/** Zustellung der letzten 30 Tage mit letzter Fehlermeldung (ohne Empfänger und Inhalte) */
export function Zustellung({ orgId }: { orgId: string }) {
  const q = useMailStats(orgId);
  const s = q.data?.[0];
  return (
    <section className="panel stack" aria-labelledby="zustellung-titel">
      <h2 id="zustellung-titel">Zustellung der Mails · 30 Tage</h2>
      {q.isPending ? <p>Wird geladen …</p> : !s ? <p>Keine Daten.</p> : (
        <>
          <p>{s.sent_30d} zugestellt · {s.failed_30d} gescheitert · {s.pending} in der Warteschlange</p>
          {s.failed_30d > 0 && <p className="tk-error">{lastFailureText(s)}</p>}
          <small>Die Agentur sieht die Einzelheiten in ihrem Postausgang.</small>
        </>
      )}
    </section>
  );
}

interface Lifecycle {
  status: string; read_only: boolean; reason: string | null; trial_ends_at: string | null; subscription_status: string | null;
  cancel_requested_at: string | null; cancel_effective_at: string | null; cancel_reason: string | null;
  early_purge_requested_at: string | null; purge_notice_sent_at: string | null;
  suspended_reason?: string | null; reduced_at?: string | null;
  /** 0444 (Steuerprüfung L17): Sperre „Aufbewahrung verlängert“; den Grund sieht nur der Betrieb */
  retention_hold_since?: string | null; retention_hold_reason?: string | null;
  plan: {
    notice_at?: string | null; purge_at?: string | null; retention_until?: string | null; next_action?: "reduce" | "purge" | "hold"; next_at?: string | null;
    country?: string | null; retention_years?: number; retention_letters_years?: number; retention_records_years?: number;
  } | null;
}

/** Kündigung, Nur-Lesen-Phase und Löschplan (0042) */
export function Lebenszyklus({ orgId }: { orgId: string }) {
  const q = useQuery({ queryKey: ["org_lifecycle_status", orgId], queryFn: () => api.read<Lifecycle>("org_lifecycle_status", { p_org: orgId }) });
  const l = q.data;
  return (
    <section className="panel stack" aria-labelledby="lebenszyklus-titel">
      <h2 id="lebenszyklus-titel">Lebenszyklus</h2>
      {q.isPending ? <p>Wird geladen …</p> : q.error || !l ? <p>Der Stand lässt sich gerade nicht laden.</p> : (
        <div className="stack">
          <p>{l.read_only ? (l.reason === "trial_expired" || l.reason === "cancelled" ? `Portal gesperrt, Export-Phase (${l.reason === "trial_expired" ? "Testphase abgelaufen" : "gekündigt"})` : `Nur lesen (${l.reason === "paused" ? "pausiert" : "gesperrt"})`) : "Aktiv"}{l.suspended_reason ? ` · Grund: ${l.suspended_reason}` : ""}</p>
          {l.cancel_requested_at && <p>Kündigung am {dt(l.cancel_requested_at)}, wirksam zum {dt(l.cancel_effective_at)}{l.cancel_reason ? ` · Grund: ${l.cancel_reason}` : ""}</p>}
          {l.reduced_at && <p>Auf die aufbewahrungspflichtigen Belege reduziert am {dt(l.reduced_at)}.</p>}
          {l.plan?.next_at && (
            <p>
              {l.plan.next_action === "reduce" ? "Reduktion auf die Belege" : "Endgültige Löschung"} geplant am {dt(l.plan.next_at)}
              {l.plan.notice_at ? ` · Ankündigung am ${dt(l.plan.notice_at)}` : ""}{l.purge_notice_sent_at ? " (versendet)" : ""}
              {l.plan.retention_until ? ` · Belege bis ${dt(l.plan.retention_until)} (${l.plan.country ?? "Land?"}: ${l.plan.retention_years ?? "–"}/${l.plan.retention_letters_years ?? "–"} Jahre${l.plan.retention_records_years && l.plan.retention_records_years !== l.plan.retention_years ? `, Zahlungsbuchungen ${l.plan.retention_records_years}` : ""})` : ""}
            </p>
          )}
          {l.early_purge_requested_at && <p className="pill warning"><span className="pill-zeichen">◷</span> Vorzeitige Löschung beantragt am {dt(l.early_purge_requested_at)}</p>}
          <AufbewahrungSperre orgId={orgId} since={l.retention_hold_since ?? null} reason={l.retention_hold_reason ?? null} />
          {!l.cancel_requested_at && !l.read_only && <small>Keine Kündigung. Die Agentur kündigt selbst unter Einstellungen · Abo.</small>}
        </div>
      )}
    </section>
  );
}

/**
 * 0444 (Steuerprüfung L17): Sperre „Aufbewahrung verlängert“ je Agentur (Betriebsprüfung, Einspruch, offene Festsetzung; § 147 Abs. 3
 * Satz 3 AO). Hält die endgültige Löschung an; Setzen und Aufheben nur mit Grund, beides steht im Protokoll (audit_log).
 */
function AufbewahrungSperre({ orgId, since, reason }: { orgId: string; since: string | null; reason: string | null }) {
  const qc = useQueryClient();
  const { busy, error, attempt } = useAction();
  const [grund, setGrund] = useState("");
  const setzen = (hold: boolean) => void attempt("retention_hold", async () => {
    await api.rpc("platform_set_retention_hold", { p_org: orgId, p_hold: hold, p_reason: grund.trim() });
    setGrund("");
    await qc.invalidateQueries({ queryKey: ["org_lifecycle_status", orgId] });
  }, hold ? "Aufbewahrung verlängert. Die endgültige Löschung ist angehalten." : "Sperre aufgehoben. Der Löschplan gilt wieder.");
  return (
    <div className="stack" role="group" aria-labelledby="aufbewahrung-titel">
      <p><strong id="aufbewahrung-titel">Aufbewahrung verlängert</strong></p>
      {since
        ? <p className="pill warning">Seit {dt(since)} gesetzt{reason ? ` · Grund: ${reason}` : ""}. Die endgültige Löschung ist angehalten.</p>
        : <small>Nicht gesetzt. Bei Betriebsprüfung, Einspruch oder offener Festsetzung setzen: Dann wird die Agentur nicht endgültig gelöscht.</small>}
      <label>Grund (Pflicht, steht im Protokoll)
        <input value={grund} maxLength={500} onChange={(e) => setGrund(e.target.value)} placeholder={since ? "z. B. Prüfung abgeschlossen, Bescheide bestandskräftig" : "z. B. Betriebsprüfung, Prüfungsanordnung vom …"} />
      </label>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        {since
          ? <button type="button" disabled={!!busy || grund.trim().length < 5} onClick={() => setzen(false)}>Sperre aufheben</button>
          : <button type="button" className="primary" disabled={!!busy || grund.trim().length < 5} onClick={() => setzen(true)}>Aufbewahrung verlängern</button>}
      </div>
    </div>
  );
}

interface DefaultRow { key: string; value: unknown; note: string | null }

/**
 * Anzeige eines Standardwerts im Feld: Listen und Objekte eingerückt (mehrzeilig lesbar), Text ohne JSON-Anführungszeichen
 * (SICHT-OEFFENTLICH-BETRIEB-22: vorher stand dort "cumulative"), sonst wie gespeichert.
 */
export function stdText(value: unknown): string {
  if (typeof value === "string") return value;
  return value !== null && typeof value === "object" ? JSON.stringify(value, null, 2) : JSON.stringify(value);
}

/**
 * Eingabe zurück in den Wert, nach dem Typ des gespeicherten Werts (SICHT-OEFFENTLICH-BETRIEB-22): Text bleibt Text (keine
 * Anführungszeichen nötig), Zahl und ja/nein werden geprüft, Listen und Objekte als JSON. Fehler als Satz fürs Feld.
 */
export function stdParse(current: unknown, text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  const s = text.trim();
  if (typeof current === "string") return { ok: true, value: s };
  if (typeof current === "boolean") return s === "true" || s === "false" ? { ok: true, value: s === "true" } : { ok: false, error: "Bitte ja oder nein wählen." };
  if (typeof current === "number") {
    const n = Number(s.replace(",", "."));
    return s !== "" && Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: "Bitte eine Zahl eingeben, zum Beispiel 14." };
  }
  try {
    return { ok: true, value: JSON.parse(s) as unknown };
  } catch {
    return { ok: false, error: "Das ist kein gültiges JSON. Listen stehen in eckigen Klammern, Texte darin in Anführungszeichen, zum Beispiel [\"de\",\"en\"]." };
  }
}

/** Produkt-Standards (platform_defaults): Werte ändern mit Typprüfung und Protokoll */
export function Plattform() {
  const qc = useQueryClient();
  // 0500: der Admin sieht und setzt nur die Betriebsfristen; Feature-Schalter, Produkt-Standards, Rechtstexte und Basiszins die Entwicklung
  const entwicklung = istEntwicklung(useKonsolenRolle());
  const q = useQuery({ queryKey: ["platform_defaults"], queryFn: () => api.select<DefaultRow>("platform_defaults", { order: [["key", "asc"]] }) });
  const { busy, error, attempt } = useAction();
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [ask, setAsk] = useState<{ row: DefaultRow; value: unknown } | null>(null);
  const [nowMs] = useState(() => Date.now());
  // Fehler am Feld (SICHT-OEFFENTLICH-BETRIEB-22): eigener Prüffehler oder die Meldung der Datenbank beim zuletzt gespeicherten Schlüssel
  const [fieldError, setFieldError] = useState<{ key: string; text: string | null } | null>(null);
  const store = (row: DefaultRow, value: unknown) => {
    setFieldError({ key: row.key, text: null });
    void attempt(`d:${row.key}`, () => api.rpc("platform_set_default", { p_key: row.key, p_value: value }), `Standard „${row.key}“ gespeichert.`)
      .then((ok) => { setAsk(null); if (ok) { setFieldError(null); setEdit((e) => { const n = { ...e }; delete n[row.key]; return n; }); void qc.invalidateQueries({ queryKey: ["platform_defaults"] }); } });
  };
  // Löschrelevante Schlüssel nur mit Rückfrage und Klartext (Befund 4)
  const save = (row: DefaultRow) => {
    const parsed = stdParse(row.value, edit[row.key] ?? stdText(row.value));
    if (!parsed.ok) return setFieldError({ key: row.key, text: parsed.error });
    if (PURGE_KEYS.includes(row.key)) setAsk({ row, value: parsed.value });
    else store(row, parsed.value);
  };
  return (
    <>
      <title>Produkt-Standards · Plattform · CreatorOffice</title>
      <h1>Produkt-Standards</h1>
      {!entwicklung && <p className="muted">Als Admin siehst du hier die Betriebsfristen (Testphase, Kulanz, Fristen bei Sperre und Löschung). Alle übrigen Standards pflegt die Entwicklung.</p>}
      <p>Diese Werte gelten, solange eine Agentur nichts Eigenes eingestellt hat. Jede Änderung steht im Protokoll. Texte ohne Anführungszeichen eingeben, Listen und Objekte als JSON.</p>
      <ZweiterFaktorBetrieb />
      {error && !fieldError && <p className="tk-error" role="alert">{error}</p>}
      {/* Je Schlüssel eine Karte statt einer Tabelle: Wert mit ausreichender Breite (JSON mehrzeilig), Speichern direkt daneben,
          Bedeutung darunter umbrochen (vorher 38 px breite Felder und ein Speichern-Knopf 2.000 px rechts; SICHT-OEFFENTLICH-BETRIEB-01) */}
      <ul className="tk-std-list" aria-label="Produkt-Standards">
        {(q.data ?? []).filter((r) => entwicklung || BETRIEBSFRISTEN.includes(r.key)).map((r) => {
          const current = stdText(r.value);
          const v = edit[r.key] ?? current;
          const bool = typeof r.value === "boolean";
          const multi = r.value !== null && typeof r.value === "object";
          const id = `std-${r.key}`;
          return (
            <li key={r.key} className="tk-std">
              <div className="tk-std-info">
                <code id={id}>{r.key}</code>
                {r.note && <small>{r.note}</small>}
                {DEFAULT_RANGES[r.key] && <small>Erlaubt: {DEFAULT_RANGES[r.key]}</small>}
              </div>
              <div className="tk-std-edit">
                {bool ? (
                  <select aria-labelledby={id} value={v} onChange={(e) => setEdit({ ...edit, [r.key]: e.target.value })}>
                    <option value="true">ja</option><option value="false">nein</option>
                  </select>
                ) : multi ? (
                  <textarea aria-labelledby={id} className="mono" spellCheck={false} rows={Math.min(10, Math.max(3, v.split("\n").length))} value={v}
                    onChange={(e) => setEdit({ ...edit, [r.key]: e.target.value })} />
                ) : <input aria-labelledby={id} value={v} onChange={(e) => setEdit({ ...edit, [r.key]: e.target.value })} />}
                <button type="button" disabled={!!busy || v === current} onClick={() => save(r)}>Speichern</button>
              </div>
              {fieldError?.key === r.key && (fieldError.text ?? error) && <small className="tk-error" role="alert">{fieldError.text ?? error}</small>}
            </li>
          );
        })}
      </ul>
      <Dialog open={!!ask} onClose={() => setAsk(null)} title="Löschrelevanten Standard ändern?">
        {ask && (
          <div className="stack">
            <p>„{ask.row.key}“ wird {JSON.stringify(ask.row.value)} → {JSON.stringify(ask.value)}. {purgeImpact(ask.row.key, ask.value, nowMs)}</p>
            <p>Unabhängig vom Standard gelten immer mindestens 90 Tage Export-Phase und 30 Tage Ankündigung.</p>
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              <button className="primary" type="button" disabled={!!busy} onClick={() => store(ask.row, ask.value)}>Ändern</button>
              <button type="button" onClick={() => setAsk(null)}>Abbrechen</button>
            </div>
          </div>
        )}
      </Dialog>
      {entwicklung && <Basiszins />}
    </>
  );
}
